/**
 * V5 详细报告 mock —— Path C：前端按"假设 schema"驱动 UI，后端跟进时只换数据源。
 *
 * 假设的 schema 扩展（提议详见 docs/archive/v5/AI-HANDOFF.md 2026-05-20 14:00 frontend 条目）：
 * 在现有 user_report_v5 内追加一个 `detailed` 块，承载 6 页详细报告的额外字段。
 * 粗略字段（overall / abilities / layers / traits / 8 LLM）原样复用，不重复。
 *
 *   user_report_v5.detailed = {
 *     ability_deep_dive:  { <ability_code>: {current_state_summary, impact_and_direction} } × 8
 *     trait_deep_dive:    [{code, behavior, ai_impact, suggested_action}] × 3
 *     trait_synthesis:    string (80-120 chars)
 *     collaboration_style: string (80-150 chars)
 *     not_recommended_tasks: string[] (3-5 items, 8-20 chars each)
 *     growth_path: {
 *       overall_goal: string (50-100),
 *       phases: [{phase_index, phase_label, phase_title, phase_goal,
 *                  learning_items[4-6], focus_ability_codes[1-3]}] × 3,
 *       follow_up_suggestions: string[] (3-5 items, 15-30 chars each),
 *       application_tips: string (80-120)
 *     }
 *   }
 *
 * ⚠️ 这只是前端的"提议"。后端确认（或修订）schema 之后，按真版本回填，adapter 不动。
 */
import { mockReportRawPayload } from './v5ReportMock';

// ============================================================================
// detailed 块（按 L2 人设合成，与 mockReportRawPayload 同一个用户视角）
// ============================================================================
const abilityDeepDive = {
  cognitive_understanding: {
    current_state_summary:
      '你对 AI 的基础原理与能力边界仍处于摸索阶段，能在直观任务上识别 AI 的可用性，但对它"不擅长什么"的判断不稳定。',
    impact_and_direction:
      '影响：容易把 AI 当万能答案。提升方向：每周回顾一次 AI 出错的情境，逐步形成自己的能力边界清单；在使用 AI 前先做一次"该不该用 AI"的快速判断。',
  },
  learning_adaptation: {
    current_state_summary:
      '面对 AI 工具更新换代，你保持开放态度并愿意试用新功能，是当前的相对优势。',
    impact_and_direction:
      '影响：能比同伴更快接触到新工具的红利。提升方向：把"看到新功能就试一下"沉淀为定期产出 —— 比如每两周记录一次新工具的可用边界，让试用转化为可复用的方法卡。',
  },
  scenario_judgment: {
    current_state_summary:
      '在常见任务上能判断 AI 是否适合用，但在跨任务边界或高不确定性场景中，判断准确率明显下降。',
    impact_and_direction:
      '影响：选错场景会让 AI 的产出价值打折。提升方向：在动手前先做任务拆解，识别"事实型 / 表达型 / 决策型"分支，再决定哪一段交给 AI、哪一段由自己来。',
  },
  instruction_interaction: {
    current_state_summary:
      '你能用清晰的自然语言与 AI 协同，迭代提示词的能力处于中上水平。',
    impact_and_direction:
      '影响：能让 AI 在多数任务上一次给出可用初稿。提升方向：把高频任务的提示词模板化、记进自己的提示词本，下次同类任务直接套用，进一步压缩协同成本。',
  },
  task_orchestration: {
    current_state_summary:
      '在两三步任务上能合理分工人机协同，但长链条任务里容易让 AI 一次性接管太多。',
    impact_and_direction:
      '影响：复杂任务的中段质量容易下滑。提升方向：把超过 3 步的任务先画一份"人机分工图"，明确每一步是人做、AI 做、还是人+AI 协同。',
  },
  result_evaluation: {
    current_state_summary:
      '大多数情况下你接受 AI 的第一版结果，事实核对和逻辑复查的动作不稳定。',
    impact_and_direction:
      '影响：错误结论可能原样进入工作产出。提升方向：给 AI 输出建立一道固定复核 —— 关键数据回到原始资料对一遍，关键结论问自己一句依据是什么。',
  },
  safety_responsibility: {
    current_state_summary:
      '在涉及客户资料、敏感信息、对外正式内容时，对 AI 边界的警觉仍偏低。',
    impact_and_direction:
      '影响：合规和数据隐私风险敞口偏高。提升方向：在工作流里固定一条"涉敏数据脱敏 → AI 处理 → 人工复审"的流程，做关键决策前明确人工把关点。',
  },
  innovation_transformation: {
    current_state_summary:
      '你愿意尝试用 AI 创造新场景，对个人方法的复用程度仍不高。',
    impact_and_direction:
      '影响：单次产出价值高，但难以规模化复用。提升方向：把跑通过一次的 AI 用法沉淀成自己的小模板（提示词 + 步骤 + 输入输出例子），让"试出来的"自然变成"用得上的"。',
  },
};

const traitDeepDive = [
  {
    code: 'ai_willingness',
    behavior:
      '你倾向于主动把 AI 接入日常工作，遇到新工具会去试一试，不等他人示范，是较高的使用意愿。',
    ai_impact:
      '高意愿能让你比同伴更早接触到 AI 红利，但也会拉高过度依赖的可能性 —— 在不该用 AI 的场景里也会先丢给 AI。',
    suggested_action:
      '维持现在的尝试节奏，但在动手前加一道"该不该用 AI"的自检；把跑得通的用法记下来，让意愿沉淀成方法。',
  },
  {
    code: 'independent_thinking',
    behavior:
      '你大多数时候会接受 AI 给出的第一版结果，较少回头追问它的依据，在重要判断上容易被它带着走。',
    ai_impact:
      '独立思考偏弱会让 AI 的错误原样进入你的产出，关键数据 / 结论的可信度受影响。',
    suggested_action:
      '在 AI 输出后固定加一步"复核"动作：关键数据回原始资料、关键结论问一句依据。涉及对外或决策的内容坚持人工通读。',
  },
  {
    code: 'proactive_learning',
    behavior:
      '你在用得上的时候会去了解新的 AI 用法，遇到问题也会查一查，学习偏被需求驱动，还没有形成定期更新的习惯。',
    ai_impact:
      '需求驱动的学习能解决眼前问题，但容易错过尚未用到、但日后会用到的能力 —— 临到需要时再补会慢半拍。',
    suggested_action:
      '给自己设一个轻量的定期更新动作：每两周看一次行业内 AI 工具新闻 / 同事用法分享，做 5-10 分钟笔记。',
  },
];

const traitSynthesis =
  '整体上你具备较高的 AI 意愿，但还没有形成"用前判断 + 用中协同 + 用后复核"的完整链路。把独立思考补到中上水平、把主动学习固化成习惯，是后续两个月最有价值的素质升级目标。';

const collaborationStyle =
  '推荐与 AI 形成"分阶段确认"协作 —— 先用 AI 出方案草稿、自己评审后再交给 AI 二轮迭代，最后由你做关键决策。涉及客户的内容由人主导、AI 辅助；事实/数据核对环节始终由人收口。';

const notRecommendedTasks = [
  '直接向 AI 提交真实客户的敏感资料',
  '将 AI 输出未审就用于对外正式内容',
  '用 AI 做有强合规要求的决策结论',
  '让 AI 一次性承担超过 3 步的高耦合任务',
];

const growthPath = {
  overall_goal:
    '用 30 天把当前的"基础应用"水平迁移到"协同优化"，重点在边界判断 + 结果复核 + 任务拆解三件事上跑通固定动作。',
  phases: [
    {
      phase_index: 1,
      phase_label: '第 1-10 天',
      phase_title: '建立边界认知与基础协同',
      phase_goal: '形成自己的 AI 能力边界清单 + 把高频任务的提示词模板化',
      learning_items: [
        '梳理 5-8 个本职工作中的常见任务，逐个标注是否适合 AI',
        '为每类适合 AI 的任务，写 1 个稳定可复用的提示词模板',
        '每周记录 2-3 次 AI 出错的情境，沉淀到自己的边界清单',
        '读 1 篇 prompt engineering 入门资料 + 看 1 个同行的高质量提示词案例',
      ],
      focus_ability_codes: ['cognitive_understanding', 'scenario_judgment'],
    },
    {
      phase_index: 2,
      phase_label: '第 11-20 天',
      phase_title: '建立结果复核与任务拆解习惯',
      phase_goal: '让"AI 输出 → 人工复核"成为不用思考就会做的固定动作',
      learning_items: [
        '给 AI 输出制定固定复核 checklist：数据 / 逻辑 / 依据三项必查',
        '挑 2 个常见的多步任务，画一份"人机分工图"再实际跑一次',
        '抽 1 次 AI 出过错的复盘，反向梳理"该在哪一步加复核"',
        '尝试在 1 个真实工作任务上把 AI 用作"试错原型"，自己做最终定稿',
      ],
      focus_ability_codes: ['result_evaluation', 'task_orchestration'],
    },
    {
      phase_index: 3,
      phase_label: '第 21-30 天',
      phase_title: '从用得上到可复用，沉淀个人方法',
      phase_goal: '把跑通过一次的 AI 用法沉淀成模板，覆盖未来类似任务',
      learning_items: [
        '为本月跑通的 AI 用法各做一份 1 页的方法卡（场景 + 提示词 + 步骤）',
        '把 2-3 个高价值的方法卡分享给同事，收集一轮反馈再迭代',
        '检查涉敏数据 / 客户资料 / 对外内容的流程，明确人工把关点',
        '为下一个月设一个轻量定期更新动作（如每两周 5-10 分钟）',
      ],
      focus_ability_codes: ['innovation_transformation', 'safety_responsibility'],
    },
  ],
  follow_up_suggestions: [
    '每月底做一次"AI 协同复盘"，看哪些任务用得好、哪些还在试错',
    '建立自己的提示词本，按任务类型分类，目标半年内积累 30+ 条',
    '关注 1-2 个行业领先公司的 AI 实践，借鉴他们的人机分工模式',
    '若团队尚无 AI 使用规范，主动牵头梳理一版小型的合规边界清单',
  ],
  application_tips:
    '不必追求一次性跑通所有动作。挑你本职工作里频次最高的 2-3 个任务作为起点，把这 30 天的方法先在它们上跑稳，再向其它任务扩展。',
};

// ============================================================================
// 完整 detailed payload：以 mockReportRawPayload 为基底 + detailed 块
// ============================================================================
const baseInner = mockReportRawPayload.user_report_v5;

export const mockReportDetailedRawPayload = {
  status: 'done',
  report_version: 5,
  is_fallback: false,
  is_detailed_unlocked: true,   // 能看到 detailed payload 意味着已解锁（后端只对 unlocked 返回 detailed 内容）
  user_report_v5: {
    ...baseInner,
    detailed: {
      ability_deep_dive: abilityDeepDive,
      trait_deep_dive: traitDeepDive,
      trait_synthesis: traitSynthesis,
      collaboration_style: collaborationStyle,
      not_recommended_tasks: notRecommendedTasks,
      growth_path: growthPath,
    },
  },
};

// 子块导出
export const mockDetailedBlock = mockReportDetailedRawPayload.user_report_v5.detailed;
